import React from "react";
import {
	App as AntdApp,
	Typography,
	Button,
	Divider,
	Input,
	Radio,
	Select,
	Space,
	Tooltip,
	Upload,
} from "antd";
import {SERIAL_READONLY_HINT} from "../constants/fields.js";
import {PlusOutlined} from "@ant-design/icons";

import FloatingField from "@shared/components/FloatingField.jsx";
import api from "@/api/axios.js";

const {TextArea} = Input;
const {Text} = Typography;

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const MAX_VIDEO_SIZE = 200 * 1024 * 1024;
const IMAGE_ACCEPT = ".webp,.gif,.png,.jpg,.jpeg";
const VIDEO_ACCEPT = ".mp4,.webm";
const IMAGE_MIME_TYPES = new Set([
	"image/webp",
	"image/gif",
	"image/png",
	"image/jpeg",
]);
const VIDEO_MIME_TYPES = new Set(["video/mp4", "video/webm"]);

const readFileAsDataUrl = (file) =>
	new Promise((resolve, reject) =>
	{
		if (!file)
		{
			resolve("");
			return;
		}
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result ?? "");
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(file);
	});

const resolveAssetUrl = (pathValue) =>
{
	if (!pathValue) return "";
	const trimmed = String(pathValue).trim();
	if (!trimmed) return "";
	if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("data:"))
		return trimmed;
	try
	{
		const base = api?.defaults?.baseURL || window.location.origin;
		return new URL(trimmed, base).href;
	}
	catch (error)
	{
		console.warn("Impossibile risolvere l'URL dell'asset", error);
		return trimmed;
	}
};

export default function FlyerBannerSection({
	rowId,
	value = {},
	onChange,
	onPendingAssetChange,
	pendingAssets = {},
	disabled = false,
	seriale,
	categoryOptions,
	categoryLoading = false,
	onCreateCategory,
})
{
	const {message} = AntdApp.useApp();
	const [imageProcessing, setImageProcessing] = React.useState(false);
	const [videoProcessing, setVideoProcessing] = React.useState(false);
	const [categorySearch, setCategorySearch] = React.useState("");
	const [bannerMode, setBannerMode] = React.useState(() =>
	{
		const hasVideo =
			Boolean(value?.banner_video) || Boolean(value?.assets?.video);
		return hasVideo ? "video" : "banner";
	});
	const [pendingImage, setPendingImage] = React.useState(null);
	const [pendingVideo, setPendingVideo] = React.useState(null);
	const rowIdRef = React.useRef(rowId);


	React.useEffect(() =>
	{
		const hasVideo =
			Boolean(value?.banner_video) || Boolean(value?.assets?.video);
		if (rowIdRef.current !== rowId)
		{
			rowIdRef.current = rowId;
			setBannerMode(hasVideo ? "video" : "banner");
			return;
		}
	}, [rowId, value?.banner_video, value?.assets?.video]);

	const bannerAssets = React.useMemo(
		() => value?.assets || {},
		[value?.assets],
	);
	const imageAssetValue = React.useMemo(() =>
	{
		const imageValue = bannerAssets?.image || "";
		if (imageValue) return imageValue;
		return bannerAssets?.gif || "";
	}, [bannerAssets?.image, bannerAssets?.gif]);

	const videoAssetValue = React.useMemo(
		() => bannerAssets?.video || "",
		[bannerAssets?.video],
	);

	const imagePreviewUrl = React.useMemo(
		() => (imageAssetValue ? resolveAssetUrl(imageAssetValue) : ""),
		[imageAssetValue],
	);

	const videoPreviewUrl = React.useMemo(
		() => (videoAssetValue ? resolveAssetUrl(videoAssetValue) : ""),
		[videoAssetValue],
	);

	const effectiveImage = pendingImage || null;
	const effectiveVideo = pendingVideo || null;

	const displayedImagePreview = React.useMemo(() =>
	{
		if (effectiveImage?.previewUrl) return effectiveImage.previewUrl;
		return imagePreviewUrl;
	}, [effectiveImage?.previewUrl, imagePreviewUrl]);

	const displayedVideoPreview = React.useMemo(() =>
	{
		if (effectiveVideo?.previewUrl) return effectiveVideo.previewUrl;
		return videoPreviewUrl;
	}, [effectiveVideo?.previewUrl, videoPreviewUrl]);

	const imageDisplayName = React.useMemo(() =>
	{
		if (effectiveImage?.file?.name) return effectiveImage.file.name;
		if (!imageAssetValue) return "";
		const fallbackName = imageAssetValue.split("/").pop() || "immagine";
		return (value?.banner && String(value.banner).trim()) || fallbackName;
	}, [effectiveImage?.file?.name, imageAssetValue, value?.banner]);

	const videoDisplayName = React.useMemo(() =>
	{
		if (effectiveVideo?.file?.name) return effectiveVideo.file.name;
		if (!videoAssetValue) return "";
		const fallbackName = videoAssetValue.split("/").pop() || "video";
		return (
			(value?.banner_video && String(value.banner_video).trim()) ||
			fallbackName
		);
	}, [effectiveVideo?.file?.name, videoAssetValue, value?.banner_video]);

	React.useEffect(() =>
	{
		setPendingImage((prev) =>
		{
			if (!pendingAssets?.image) return null;
			if (prev === pendingAssets.image) return prev;
			return pendingAssets.image;
		});
	}, [pendingAssets?.image, rowId]);

	React.useEffect(() =>
	{
		setPendingVideo((prev) =>
		{
			if (!pendingAssets?.video) return null;
			if (prev === pendingAssets.video) return prev;
			return pendingAssets.video;
		});
	}, [pendingAssets?.video, rowId]);

	const handleBannerTypeChange = React.useCallback((event) =>
	{
		const nextMode = event?.target?.value || "banner";
		setBannerMode(nextMode);
	}, []);

	const categoryFilterOption = React.useCallback((input, option) =>
			(option?.searchValue ?? "").includes(String(input).toLowerCase()),
		[]
	);

	const handleAddCategory = React.useCallback(async () =>
	{
		const trimmed = categorySearch.trim();
		if (!trimmed) return;
		try
		{
			const created = await onCreateCategory?.(trimmed);
			if (created?.value) onChange?.("categoria", created.value);
		}
		finally
		{
			setCategorySearch("");
		}
	}, [categorySearch, onChange, onCreateCategory]);

	const handleManualAssetChange = React.useCallback((assetType, assetValue) =>
		{
			const normalized = assetValue ?? "";
			if (assetType === "image")
			{
				onChange?.("assets.image", normalized);
				const isGif =
					typeof normalized === "string" &&
					normalized.toLowerCase().trim().endsWith(".gif");
				onChange?.("assets.gif", isGif ? normalized : "");
				return;
			}
			if (assetType === "video") onChange?.("assets.video", normalized);
		},
		[onChange],
	);

	const validateBeforeUpload = React.useCallback((assetType, file) =>
		{
			const isImage = assetType === "image";

			if (disabled) return Upload.LIST_IGNORE;
			if (
				(isImage && bannerMode !== "banner") ||
				(!isImage && bannerMode !== "video")
			)
			{
				message.error(
					isImage
						? "Seleziona l'opzione Banner per caricare un'immagine"
						: "Seleziona l'opzione Banner video per caricare un video",
				);
				return Upload.LIST_IGNORE;
			}

			const label = isImage
				? String(value?.banner || "").trim()
				: String(value?.banner_video || "").trim();

			if (!label)
			{
				message.error(
					isImage
						? "Compila il campo Banner prima di caricare un'immagine"
						: "Compila il campo Banner video prima di caricare un video",
				);
				return Upload.LIST_IGNORE;
			}

			const allowedTypes = isImage ? IMAGE_MIME_TYPES : VIDEO_MIME_TYPES;

			if (!allowedTypes.has(file.type))
			{
				message.error(
					isImage
						? "Formato non supportato. Usa file WEBP, GIF, PNG o JPG."
						: "Formato non supportato. Usa file MP4 o WEBM.",
				);
				return Upload.LIST_IGNORE;
			}

			const limit = isImage ? MAX_IMAGE_SIZE : MAX_VIDEO_SIZE;

			if (file.size > limit)
			{
				message.error(
					isImage
						? "Immagine troppo grande (max 10MB)."
						: "Video troppo grande (max 200MB).",
				);
				return Upload.LIST_IGNORE;
			}

			return true;
		},
		[bannerMode, disabled, message, value?.banner, value?.banner_video],
	);

	const notifyPendingChange = React.useCallback((assetType, payload) =>
	{
		onPendingAssetChange?.(assetType, payload);
	}, [onPendingAssetChange]);

	const handlePendingAssetSelection = React.useCallback(async (assetType, file) =>
	{
		const isImage = assetType === "image";
		const label = isImage
			? String(value?.banner || "").trim()
			: String(value?.banner_video || "").trim();
		if (!label)
		{
			message.error(
				isImage
					? "Compila il campo Banner prima di caricare un'immagine"
					: "Compila il campo Banner video prima di caricare un video",
			);
			return;
		}

		try
		{
			if (isImage) setImageProcessing(true);
			else setVideoProcessing(true);

			const previewUrl = await readFileAsDataUrl(file);
			const pendingData = {file, previewUrl, label};
			if (isImage)
			{
				setPendingImage(pendingData);
				handleManualAssetChange("image", "");
			}
			else
			{
				setPendingVideo(pendingData);
				handleManualAssetChange("video", "");
			}
			notifyPendingChange(assetType, pendingData);
			message.success(
				isImage
					? "Immagine pronta per il salvataggio"
					: "Video pronto per il salvataggio",
			);
		}
		catch (error)
		{
			console.warn("Impossibile preparare l'asset selezionato", error);
			message.error("Impossibile leggere il file selezionato");
		}
		finally
		{
			if (isImage) setImageProcessing(false);
			else setVideoProcessing(false);
		}
	}, [handleManualAssetChange, message, notifyPendingChange, value?.banner, value?.banner_video]);

	const beforeUploadWithPending = React.useCallback((assetType, file) =>
	{
		const validation = validateBeforeUpload(assetType, file);
		if (validation !== true) return validation;
		void handlePendingAssetSelection(assetType, file);
		return Upload.LIST_IGNORE;
	}, [handlePendingAssetSelection, validateBeforeUpload]);

	const previewUploadFile = React.useCallback(async (file) =>
	{
		if (!file) return "";
		if (file.thumbUrl) return file.thumbUrl;
		if (file.url) return file.url;

		try
		{
			const source = file.originFileObj || file;
			return await readFileAsDataUrl(source);
		}
		catch (error)
		{
			console.warn("Impossibile generare l'anteprima dell'immagine", error);
			return "";
		}
	}, []);

	const handleImagePreview = React.useCallback((file) =>
	{
		const previewUrl = file?.url || file?.thumbUrl;
		if (!previewUrl) return;

		try
		{
			window.open(previewUrl, "_blank", "noopener,noreferrer");
		}
		catch (error)
		{
			console.warn("Impossibile aprire l'anteprima dell'immagine", error);
		}
	}, []);

	const handleAssetRemove = React.useCallback(
		(assetType) =>
		{
			const isImage = assetType === "image";
			const assetPath = isImage ? imageAssetValue : videoAssetValue;
			const pendingValue = isImage ? pendingImage : pendingVideo;

			handleManualAssetChange(assetType, "");
			if (isImage) setPendingImage(null);
			else setPendingVideo(null);
			notifyPendingChange(assetType, null);

			if (!assetPath && !pendingValue) return true;

			message.open({
				type: "info",
				content: isImage
					? "Immagine contrassegnata per la rimozione. Ricorda di salvare."
					: "Video contrassegnato per la rimozione. Ricorda di salvare.",
			});
			return true;
		},
		[
			handleManualAssetChange,
			imageAssetValue,
			message,
			notifyPendingChange,
			pendingImage,
			pendingVideo,
			videoAssetValue,
		],
	);

	const handleImageRemove = React.useCallback(
		() => handleAssetRemove("image"),
		[handleAssetRemove],
	);


	const handleVideoRemove = React.useCallback(
		() => handleAssetRemove("video"),
		[handleAssetRemove],
	);

	return (
		<>
			<div className="fm-col">
				{/* Seriale in sola lettura: vedi FlyerProductSection (si cambia in pre-elaborazione). */}
				<Tooltip title={SERIAL_READONLY_HINT} mouseEnterDelay={.3}>
					<div>
						<FloatingField label="Seriale">
							<Input placeholder="Seriale" value={seriale ?? ""} readOnly variant="borderless"/>
						</FloatingField>
					</div>
				</Tooltip>

				<FloatingField label="Titolo">
					<Input
						placeholder="Titolo"
						value={value?.titolo || ""}
						onChange={(event) =>
							onChange?.("titolo", event.target.value)
						}
						disabled={disabled}
					/>
				</FloatingField>

				<FloatingField label="Link">
					<Input
						placeholder="Link"
						value={value?.link || ""}
						onChange={(event) =>
							onChange?.("link", event.target.value)
						}
						disabled={disabled}
					/>
				</FloatingField>

				<FloatingField label="Descrizione">
					<TextArea
						placeholder="Descrizione"
						autoSize={{minRows: 2, maxRows: 4}}
						value={value?.descrizione || ""}
						onChange={(event) =>
							onChange?.("descrizione", event.target.value)
						}
						disabled={disabled}
					/>
				</FloatingField>
			</div>

			<div className="fm-col">
				<Text strong>
					Tipologia contenuto:
					<Radio.Group
						value={bannerMode}
						onChange={handleBannerTypeChange}
						disabled={disabled}
					>
						<Radio.Button value="banner">Banner</Radio.Button>
						<Radio.Button value="video">Banner video</Radio.Button>
					</Radio.Group>
				</Text>

				{bannerMode === "banner" ? (
					<FloatingField label="Banner">
						<Input
							placeholder="Banner"
							value={value?.banner || ""}
							onChange={(event) =>
								onChange?.("banner", event.target.value)
							}
							disabled={disabled}
						/>
					</FloatingField>
				) : (
					<FloatingField label="Banner video">
						<Input
							placeholder="Banner video"
							value={value?.banner_video || ""}
							onChange={(event) =>
								onChange?.(
									"banner_video",
									event.target.value,
								)
							}
							disabled={disabled}
						/>
					</FloatingField>
				)}

				<FloatingField label="Banner collegato">
					<Input
						placeholder="Banner collegato"
						value={value?.banner_collegato || ""}
						onChange={(event) =>
							onChange?.("banner_collegato", event.target.value)
						}
						disabled={disabled}
					/>
				</FloatingField>

				<FloatingField label="Categoria">
					<Select
						showSearch
						allowClear
						placeholder="Categoria"
						options={categoryOptions}
						value={value?.categoria ? String(value.categoria) : undefined}
						filterOption={categoryFilterOption}
						onChange={(optionValue) => onChange?.("categoria", optionValue || null)}
						loading={categoryLoading}
						onSearch={setCategorySearch}
						onInputKeyDown={(e) =>
						{
							if (e.key === "Enter")
							{
								const trimmed = categorySearch.trim();
								if (trimmed && onCreateCategory && !categoryOptions?.some((opt) => (opt?.raw?.name || opt?.value || "").toLowerCase() === trimmed.toLowerCase()))
								{
									e.preventDefault();
									e.stopPropagation();
									handleAddCategory();
								}
							}
						}}
						onOpenChange={(open) =>
						{
							if (!open) setCategorySearch("");
						}}
						notFoundContent={categoryLoading ? "Caricamento..." : "Nessuna categoria"}
						popupRender={(menu) => (
							<>
								{menu}
								{categorySearch &&
									onCreateCategory &&
									!categoryOptions?.some(
										(opt) =>
											(opt?.raw?.name || opt?.value || "").toLowerCase() ===
											categorySearch.toLowerCase(),
									) && (
										<>
											<Divider style={{margin: "8px 0"}}/>
											<Space style={{padding: "0 8px 4px"}}>
												<Button
													type="text"
													size="small"
													icon={<PlusOutlined/>}
													onMouseDown={(event) => event.preventDefault()}
													onClick={handleAddCategory}
													disabled={disabled}
												>
													{`Aggiungi "${categorySearch}"`}
												</Button>
											</Space>
										</>
									)}
							</>
						)}
						disabled={disabled}
					/>
				</FloatingField>

				{bannerMode === "banner" ? (
					<>
						<FloatingField label="Immagine (WEBP, GIF, PNG, JPG)">
							<Space direction="vertical" size={8} style={{width: "100%"}}>
								<Upload
									accept={IMAGE_ACCEPT}
									maxCount={1}
									fileList={[]}
									listType="picture"
									beforeUpload={(file) => beforeUploadWithPending("image", file)}
									onRemove={handleImageRemove}
									previewFile={previewUploadFile}
									onPreview={handleImagePreview}
									showUploadList={false}
									disabled={
										disabled || bannerMode !== "banner" || imageProcessing
									}
								>
									<Button
										block
										type="dashed"
										icon={<PlusOutlined/>}
										loading={imageProcessing}
									>
										Carica immagine
									</Button>
								</Upload>
							</Space>
						</FloatingField>
					</>
				) : (
					<>
						<FloatingField label="Video (MP4, WEBM)">
							<Space
								direction="vertical"
								size={8}
								style={{width: "100%"}}
							>
								<Upload
									accept={VIDEO_ACCEPT}
									maxCount={1}
									fileList={[]}
									beforeUpload={(file) => beforeUploadWithPending("video", file)}
									onRemove={handleVideoRemove}
									showUploadList={false}
									disabled={
										disabled ||
										bannerMode !== "video" ||
										videoProcessing
									}
								>
									<Button
										block
										type="dashed"
										icon={<PlusOutlined/>}
										loading={videoProcessing}
									>
										Carica video
									</Button>
								</Upload>
							</Space>
						</FloatingField>
					</>
				)}
			</div>

			<div className="fm-col">
				{
					bannerMode === "banner"
						? (displayedImagePreview && (
							<div className="fm-banner-preview" style={{width: "100%"}}>
								<div className="fm-banner-preview-media"
									 style={{
										 width: "100%",
										 borderRadius: 8,
										 overflow: "hidden",
										 border: "1px solid #f0f0f0",
										 textAlign: "center",
									 }}
								>
									{imageDisplayName ? (
										<Text strong>{imageDisplayName}</Text>
									) : null}
									<img
										src={displayedImagePreview}
										alt="Anteprima banner"
										style={{
											display: "block",
											width: "100%",
										}}
									/>
								</div>
								<Button
									type="text"
									danger
									onClick={() =>
									{
										void handleImageRemove();
									}}
									disabled={
										disabled ||
										imageProcessing
									}
								>
									Rimuovi immagine
								</Button>
							</div>
						))
						: (displayedVideoPreview && (
							<div className="fm-banner-preview" style={{width: "100%"}}>
								<div
									className="fm-banner-preview-media"
									style={{
										width: "100%",
										borderRadius: 8,
										overflow: "hidden",
										border: "1px solid #f0f0f0",
									}}
								>
									{videoDisplayName ? (
										<Text strong>{videoDisplayName}</Text>
									) : null}
									<video
										src={displayedVideoPreview}
										controls
										style={{
											display: "block",
											width: "100%",
										}}
									/>
								</div>
								<Button
									type="text"
									danger
									onClick={() =>
									{
										void handleVideoRemove();
									}}
									disabled={
										disabled ||
										videoProcessing
									}
								>
									Rimuovi video
								</Button>
							</div>
						))
				}
			</div>
		</>
	);
}

export {IMAGE_ACCEPT, VIDEO_ACCEPT, IMAGE_MIME_TYPES, VIDEO_MIME_TYPES};
